/**
 * Espace super-admin (v0.2) : AdminLayout scopé (data-theme="admin") + routes.
 * Ligne verte en haut, menu latéral (ordre + séparateurs + compteurs + pied avec version),
 * contenu à droite. Monté hors des providers hors-ligne / mode public.
 */
import { useEffect, useState } from 'react';
import { Routes, Route, NavLink, Navigate, useNavigate } from 'react-router-dom';
import { adminApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { CavoramaLogo } from '../../components/CavoramaLogo';
import { APP_VERSION } from './constants';
import Appellations from '../vignobles/Appellations';
import Dashboard from './pages/Dashboard';
import Tenants from './pages/Tenants';
import Users from './pages/Users';
import Logs from './pages/Logs';
import Settings from './pages/Settings';
import ForcePasswordChange from '../auth/ForcePasswordChange';

function NavItem({ to, label, count, error }) {
  return (
    <NavLink to={to} end={to === '/admin'} className={({ isActive }) => `adm-nav-item ${isActive ? 'adm-nav-active' : ''}`} data-testid={`nav-${to.split('/').pop() || 'dashboard'}`}>
      <span>{label}</span>
      {count != null && <span className={`text-xs ${error ? 'adm-text-error' : 'text-n500'}`}>{count}</span>}
    </NavLink>
  );
}

export default function AdminApp() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [meta, setMeta] = useState({ tenants: 0, users: 0, appellations: 0, errors_24h: 0, version: APP_VERSION });
  useEffect(() => { adminApi.meta().then(setMeta).catch(() => {}); }, []);
  const Sep = () => <div className="hair-b my-2 opacity-60" />;

  return (
    <div data-theme="admin" className="min-h-screen" style={{ background: 'var(--paper)' }}>
      <div className="adm-topline" />
      <ForcePasswordChange />
      <div className="grid min-h-screen" style={{ gridTemplateColumns: '240px 1fr' }}>
        <aside className="adm-side hair-r flex flex-col p-4 pt-5 h-screen sticky top-0">
          <div className="shrink-0">
            <CavoramaLogo className="h-7" testid="admin-brand" />
            <span className="adm-pill mt-2 inline-block" data-testid="super-admin-pill">Super admin</span>
          </div>
          <nav className="mt-5 flex-1 overflow-y-auto min-h-0" data-testid="admin-nav">
            <NavItem to="/admin" label="Dashboard" />
            <Sep />
            <NavItem to="/admin/tenants" label="Établissements" count={meta.tenants} />
            <NavItem to="/admin/users" label="Utilisateurs" count={meta.users} />
            <Sep />
            <NavItem to="/admin/appellations" label="Appellations" count={meta.appellations} />
            <Sep />
            <NavItem to="/admin/logs" label="Journal" count={meta.errors_24h > 0 ? `${meta.errors_24h} err.` : null} error={meta.errors_24h > 0} />
            <NavItem to="/admin/settings" label="Paramètres" />
          </nav>
          <div className="shrink-0 pt-3 mt-2 hair-t">
            <p className="text-xs text-n600">Super Admin</p>
            <p className="text-xs text-n500 truncate adm-mono">{user?.email}</p>
            <button className="vd-link text-left text-sm mt-1" onClick={() => { logout(); navigate('/login'); }} data-testid="admin-logout">Se déconnecter</button>
            <p className="text-[11px] text-n500 mt-2">Cavorama Admin · {APP_VERSION}</p>
          </div>
        </aside>
        <main className="relative overflow-auto h-screen p-6 md:p-8">
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/tenants" element={<Tenants />} />
            <Route path="/users" element={<Users />} />
            <Route path="/appellations" element={<div className="h-full min-h-[70vh]"><Appellations canEditIndex /></div>} />
            <Route path="/logs" element={<Logs />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="*" element={<Navigate to="/admin" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}
